import { useMemoQueriesResults } from '@chronosphereio/shared';
import { UseQueryOptions, useQueries, useQuery } from '@tanstack/react-query';
import { createContext, useContext } from 'react';
import { DefaultPluginKinds, PluginImplementation, PluginMetadata, PluginType } from '../model';

export interface PluginRegistryContextType {
  getPlugin<T extends PluginType>(pluginType: T, kind: string): Promise<PluginImplementation<T>>;
  listPluginMetadata(pluginType: PluginType): Promise<PluginMetadata[]>;
  defaultPluginKinds: DefaultPluginKinds;
}

export const PluginRegistryContext = createContext<PluginRegistryContextType | undefined>(undefined);

/**
 * Use the PluginRegistry context directly. This is meant as an escape hatch for custom async flows. You should probably
 * be using `usePlugin` or `useListPluginMetadata` instead.
 */
export function usePluginRegistry() {
  const ctx = useContext(PluginRegistryContext);
  if (ctx === undefined) {
    throw new Error('PluginRegistryContext not found. Did you forget a provider?');
  }
  return ctx;
}

// Allows consumers to pass useQuery options from react-query when loading a plugin
type UsePluginOptions<T extends PluginType> = Omit<
  UseQueryOptions<PluginImplementation<T>, Error, PluginImplementation<T>, [string, PluginType, string]>,
  'queryKey' | 'queryFn'
>;

/**
 * Loads a plugin and returns the plugin implementation, along with loading/error state.
 */
export function usePlugin<T extends PluginType>(pluginType: T, kind: string, options?: UsePluginOptions<T>) {
  // We never want to ask for a plugin when the kind isn't set yet, so disable those queries automatically
  options = {
    ...options,
    enabled: (options?.enabled ?? true) && kind !== '',
  };
  const { getPlugin } = usePluginRegistry();
  return useQuery(['getPlugin', pluginType, kind], () => getPlugin(pluginType, kind), options);
}

/**
 * Loads a list of plugins and returns the plugin implementation, along with loading/error state.
 */
export function usePlugins<T extends PluginType>(pluginType: T, plugins: Array<{ kind: string }>) {
  const { getPlugin } = usePluginRegistry();
  return useMemoQueriesResults(
    useQueries({
      queries: plugins.map((p) => {
        return {
          queryKey: ['getPlugin', pluginType, p.kind],
          queryFn: () => getPlugin(pluginType, p.kind),
        };
      }),
    })
  );
}

// Allow consumers to pass useQuery options from react-query when listing metadata
type UseListPluginMetadataOptions = Omit<
  UseQueryOptions<PluginMetadata[], Error, PluginMetadata[], [string, PluginType]>,
  'queryKey' | 'queryFn'
>;

/**
 * Gets a list of plugin metadata for the specified plugin type and returns it, along with loading/error state.
 */
export function useListPluginMetadata(pluginType: PluginType, options?: UseListPluginMetadataOptions) {
  const { listPluginMetadata } = usePluginRegistry();
  return useQuery(['listPluginMetadata', pluginType], () => listPluginMetadata(pluginType), options);
}
